import React, { useState } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import { Stack, Flex, Text, Grid } from '@atlaskit/primitives/compiled';
import Button from '@atlaskit/button/new';

const i18n = defineMessages({
	anonymousUser: {
		id: 'content-history-new-contributors-overflow.contributors-overflow.anonymous.user',
		defaultMessage: 'Anonymous user',
		description: 'Display name for anonymous user',
	},
	userOverflow: {
		id: 'content-history-new-contributors-overflow.contributors-overflow.user.overflow',
		defaultMessage: '+ {userCount} {userCount, plural, one {other} other {others}}',
		description:
			'When there are over 6 users, will show this message in a button which users can click to expand to show remaining users',
	},
	showLess: {
		id: 'content-history-new-contributors-overflow.contributors-overflow.user.overflow.show.less',
		defaultMessage: 'Show less',
		description: 'Button to show less users',
	},
});

const styles = cssMap({
	buttonWrapper: {
		width: '100px',
	},
	avatarStack: {
		gap: token('space.100'),
	},
	avatarGrid: {
		gap: token('space.100'),
		gridTemplateColumns: '1fr 1fr',
	},
});

type Contributor = {
	name?: string;
	src?: string;
};

type NewContributorComponentProps = {
	contributors: Contributor[];
	maxShownUsers?: number;
	shouldUseGridStyles?: boolean;
};

const MAX_SHOWN_USERS = 6;

export const ContributorsOverflow = ({
	contributors,
	maxShownUsers = MAX_SHOWN_USERS,
	shouldUseGridStyles,
}: NewContributorComponentProps) => {
	const intl = useIntl();
	const anonymousUser = intl.formatMessage(i18n.anonymousUser);
	const [showOverflow, setShowOverflow] = useState<boolean>(false);

	const remainingUserCount = contributors.length - maxShownUsers;

	const UserAvatar = ({ user }: { user: Contributor }) => (
		<Flex gap="space.050" alignItems="center">
			<Avatar
				testId="version-history-avatar"
				size="xsmall"
				src={user?.src}
				name={user?.name || anonymousUser}
				borderColor={token('color.background.neutral.subtle')}
			/>
			<Text testId="version-history-display-name" color="color.text.subtle">
				{user?.name || anonymousUser}
			</Text>
		</Flex>
	);

	const userInfo = contributors
		.slice(0, maxShownUsers)
		.map((user, index) => <UserAvatar key={`displayed-user-${index}`} user={user} />);

	const overflowUserInfo = contributors
		.slice(maxShownUsers)
		.map((user, index) => <UserAvatar key={`overflow-user-${index}`} user={user} />);

	const AvatarsWrapper = shouldUseGridStyles ? Grid : Stack;

	return (
		<Stack space="space.100">
			<AvatarsWrapper
				testId={`avatars-wrapper-${shouldUseGridStyles ? 'grid' : 'stack'}`}
				xcss={shouldUseGridStyles ? styles.avatarGrid : styles.avatarStack}
			>
				{userInfo}
				{showOverflow && overflowUserInfo}
			</AvatarsWrapper>
			{remainingUserCount > 0 && (
				<Flex xcss={cx(styles.buttonWrapper)}>
					<Button
						testId="overflow-button"
						spacing="compact"
						onClick={(event) => {
							event.stopPropagation(); // Prevent click on parent section button when we click the contributors button
							setShowOverflow((prevShowOverflow) => !prevShowOverflow);
						}}
					>
						{showOverflow
							? intl.formatMessage(i18n.showLess)
							: intl.formatMessage(i18n.userOverflow, { userCount: remainingUserCount })}
					</Button>
				</Flex>
			)}
		</Stack>
	);
};
